import { ListItemText, Menu, MenuItem } from '@mui/material';
import { Badge, colors, Typography } from '@sweep-io/sweep-design';
import pluralize from 'pluralize';
import { useFetchConfigurationItem } from '../../dependencies/useFetchConfigurationItem';
import { useCrmOrgsApiFacade } from '../../../../../../apis/facades/useCrmOrgsApiFacade';
import { ReactNode, useCallback, useState } from 'react';
import { ConfigurationType } from '../../../../../documentation/dependencies/types';
import { translateNewConfigurationTypeKeysToOldVersion } from '../../../../../documentation/dependencies/helpers';
import groupBy from 'lodash/groupBy';
import { isMetadataElementInCanvas } from '../helper';
import {
  ConfigurationDependencies,
  ConfigurationDependenciesWithParentType,
  ConfigurationInstanceData,
  FieldMetadataRecordProperties,
  FlowMetadataRecordProperties,
} from '../../../../../documentation/dependencies/DependenciesTypes';
import { getMetadataLabel } from '../../../../../documentation/tabs/getMetadataLabel';
import { useRunOnce } from '../../../../../common/useRunOnce';
import {
  FullMetadataElement,
  useAddMetadataElementToCanvas,
} from '../../../metadata-elements/useAddMetadataElementToCanvas';
import { CenteredCircularProgress } from '../../../../../common/CenteredCircularProgress';
import { telemetry } from '../../../../../../telemetry';
import { Check } from '@sweep-io/sweep-design/dist/icons';

interface MetadataDependenciesMenuProps {
  onClose: () => void;
  anchorEl: (EventTarget & HTMLLIElement) | null;
  funnelMap: FunnelMap;
  fullMetadataId: string;
  closeAll: () => void;
}
type Dependencies = Partial<ConfigurationDependencies> & {
  [key: string]: (
    | ConfigurationInstanceData
    | FieldMetadataRecordProperties
    | FlowMetadataRecordProperties
  )[];
};

type DependenciesWithParentType = Partial<ConfigurationDependenciesWithParentType> & {
  [key: string]: (
    | (ConfigurationInstanceData & { parentType: string })
    | (FieldMetadataRecordProperties & { parentType: string })
    | (FlowMetadataRecordProperties & { parentType: string })
  )[];
};

const stateToMessage: Record<string, { message: string; icon?: ReactNode }> = {
  loading: { message: 'Loading...', icon: <CenteredCircularProgress size={24} /> },
  noDependencies: { message: 'Element has no dependencies' },
  allDependenciesAdded: {
    message: 'All dependencies added to the canvas',
    icon: <Check color={colors.grey[900]} />,
  },
  error: { message: 'Error fetching dependencies' },
  hasDependencies: {
    message: 'Add dependencies to the canvas',
  },
};

export const MetadataDependenciesMenu = ({
  onClose,
  anchorEl,
  funnelMap,
  fullMetadataId,
  closeAll,
}: MetadataDependenciesMenuProps) => {
  const [dependencies, setDependencies] = useState<DependenciesWithParentType>({});
  const { crmOrgId, metadataId: originMetadataId } = funnelMap.metadataElements[fullMetadataId];
  const [state, setState] = useState<
    'loading' | 'error' | 'hasDependencies' | 'noDependencies' | 'allDependenciesAdded'
  >('loading');

  const { get_crmOrgConfigurationDependenciesFromSalto } = useCrmOrgsApiFacade();
  const { fetchConfigurationItem } = useFetchConfigurationItem();

  useRunOnce(async () => {
    try {
      const item = await fetchConfigurationItem(originMetadataId, crmOrgId);

      const dependencies = await get_crmOrgConfigurationDependenciesFromSalto({
        crmOrgId,
        configurationName: item.name,
        filename: item.filename,
        objectName: '',
        configurationKey: '',
      });

      const { [ConfigurationType.others]: others, ...dependenciesWithoutOthers } = dependencies;

      const itemsWithTranslatedKeys = others.map((other) => ({
        ...other,
        type: translateNewConfigurationTypeKeysToOldVersion(other.type as string),
      }));
      const groupedItems = groupBy(itemsWithTranslatedKeys, 'type');
      const _dependencies: Dependencies = { ...dependenciesWithoutOthers };

      // merge grouped items with dependencies without others
      Object.keys(groupedItems).forEach((key) => {
        if (key in _dependencies) {
          _dependencies[key] = [..._dependencies[key], ...groupedItems[key]];
        } else {
          _dependencies[key] = groupedItems[key];
        }
      });

      // Filter all items to only include those that are not already in canvas
      const filteredDependencies: DependenciesWithParentType = Object.fromEntries(
        Object.entries(_dependencies)
          .map(([key, value]) => [
            key,
            value
              .filter(
                (item) =>
                  !isMetadataElementInCanvas({
                    metadataId: item.metadataId,
                    crmOrgId,
                    canvasFunnelMap: funnelMap,
                  }),
              )
              .map((item) => ({ ...item, parentType: key })),
          ])
          .filter(([, value]) => value.length > 0),
      );

      setDependencies(filteredDependencies);
      const _dependenciesCount = Object.values(_dependencies).flat().length;
      const filteredDependenciesCount = Object.values(filteredDependencies).flat().length;

      if (filteredDependenciesCount > 0) {
        setState('hasDependencies');
      } else {
        if (_dependenciesCount > 0) {
          setState('allDependenciesAdded');
        } else {
          setState('noDependencies');
        }
      }
    } catch (error) {
      telemetry.captureError(error);
      console.error('Error fetching dependencies:', error);
      setState('error');
    }
  });

  const { addMetadataElementsToCanvasAndEnrichConnections } = useAddMetadataElementToCanvas();

  const addDependenciesToCanvas = useCallback(
    (
      dependencies: (
        | (ConfigurationInstanceData & { parentType: string })
        | (FieldMetadataRecordProperties & { parentType: string })
        | (FlowMetadataRecordProperties & { parentType: string })
      )[],
    ) => {
      const metadataElements: FullMetadataElement[] = dependencies.map((dependency, index) => ({
        name: dependency.label,
        type: dependency.type,
        crmOrgId,
        metadataId: dependency.metadataId,
        parentType: dependency.parentType,
        position: {
          column: 0,
          row: -index,
        },
      }));
      addMetadataElementsToCanvasAndEnrichConnections({
        metadataElements,
        canvasFunnelMap: funnelMap,
        parentElement: {
          metadataId: originMetadataId,
          crmOrgId,
        },
      });
    },
    [addMetadataElementsToCanvasAndEnrichConnections, funnelMap, originMetadataId, crmOrgId],
  );

  const renderContent = () => {
    if (state === 'hasDependencies') {
      return (
        <>
          {Object.entries(dependencies).map(([key, value]) => (
            <CustomMenuItem
              key={key}
              onClick={() => {
                addDependenciesToCanvas(
                  value as (ConfigurationInstanceData & { parentType: string })[],
                );
                closeAll();
              }}
              disabled={value.length === 0}
              icon={<Badge variant="secondary" label={value.length} />}
            >
              {pluralize(getMetadataLabel(key), value.length)}
            </CustomMenuItem>
          ))}
        </>
      );
    }

    return (
      <CustomMenuItem onClick={() => {}} disabled icon={stateToMessage[state].icon}>
        {stateToMessage[state].message}
      </CustomMenuItem>
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '.MuiListItemIcon-root': {
          minWidth: '24px',
        },
        '& li': {
          paddingLeft: '8px',
        },
      }}
    >
      {renderContent()}
    </Menu>
  );
};

const CustomMenuItem = ({
  children,
  disabled,
  onClick,
  icon,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
  icon?: ReactNode;
}) => {
  return (
    <MenuItem onClick={onClick} sx={{ minWidth: '252px' }} disabled={disabled}>
      <ListItemText
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body" color={colors.grey[900]}>
          {children}
        </Typography>
        {icon}
      </ListItemText>
    </MenuItem>
  );
};
